import { clsx } from 'clsx';
import { forwardRef } from 'react';
import { Button as RAC_Button } from 'react-aria-components';
import { Spinner } from '~/components/Spinner/Spinner';
import styles from './Button.module.css';
import type { ButtonProps } from './Button.types';

/**
 * Button component that forwards refs to the underlying React Aria Button.
 * This is particularly useful for components that need to reference the button
 * for positioning (like Popovers) or focus management.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      ariaLabel,
      isDisabled,
      isTransparent,
      isUnderlined,
      size,
      variant = 'primary',
      isPill,
      isPillShape,
      children,
      noWrap,
      isLoading,
      dataTestId,
      minimalPadding = false,
      ...props
    },
    ref
  ) => {
    const buttonStyles = clsx(styles.button, {
      [styles.primary]: variant === 'primary',
      [styles.secondary]: variant === 'secondary',
      [styles.tertiary]: variant === 'tertiary',
      [styles.neutral]: variant === 'neutral',
      [styles.icon]: variant === 'icon',
      [styles.bare]: variant === 'bare',
      [styles.asLink]: variant === 'asLink',
      [styles.pill]: isPill || variant === 'pill',
      [styles.pillShape]: isPillShape,
      [styles.pillLegacy]: variant === 'pill',
      [styles.small]: size === 'small',
      [styles.extraSmall]: size === 'extraSmall',
      [styles.fullWidth]: size === 'fullWidth',
      [styles.responsive]: size === 'responsive',
      [styles.noWrap]: noWrap,
      [styles.transparent]: isTransparent,
      [styles.underlined]: isUnderlined,
      [styles.minimalPadding]: minimalPadding
    });

    if (typeof isLoading === 'undefined')
      return (
        <RAC_Button
          {...props}
          ref={ref}
          data-test-id={dataTestId ?? 'Button'}
          className={buttonStyles}
          isDisabled={isDisabled}
          aria-label={ariaLabel}
        >
          {children}
        </RAC_Button>
      );

    return (
      <RAC_Button
        {...props}
        ref={ref}
        data-test-id={dataTestId ?? 'Button'}
        className={buttonStyles}
        isDisabled={isDisabled || isLoading}
        aria-label={ariaLabel}
        aria-busy={isLoading}
      >
        {isLoading ? (
          <>
            <span className={clsx(styles.content, { [styles.hidden]: isLoading })}>{children}</span>
            <span className={styles.spinner}>
              <Spinner />
            </span>
          </>
        ) : (
          children
        )}
      </RAC_Button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
